import Link from 'next/link';
import React from 'react';
import { cn } from '../../lib/utils';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xl2';
  responsiveSize?: {
    mobile?: 'sm' | 'md' | 'lg' | 'xl' | 'xl2';
    tablet?: 'sm' | 'md' | 'lg' | 'xl' | 'xl2';
    desktop?: 'sm' | 'md' | 'lg' | 'xl' | 'xl2';
  };
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  responsiveSize,
  href,
  onClick,
  disabled = false,
  className,
  type = 'button',
  fullWidth = false,
}) => {
  const baseClasses = 'group relative inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 active:transform';

  const variantClasses = {
    primary: 'text-white hover:text-primary',
    secondary: 'text-gray-600 hover:text-primary',
  };

  const sizeClasses = {
    sm: 'px-5 py-2 text-xs',
    md: 'px-6 py-2.5 text-sm',
    lg: 'px-8 py-3 text-base',
    xl: 'px-8 py-3 text-lg',
    xl2: 'px-8 py-3 text-xl'
  };

  // Generate responsive size classes
  const getResponsiveSizeClasses = () => {
    if (!responsiveSize) return sizeClasses[size];

    let classes = '';

    // Mobile (default)
    if (responsiveSize.mobile) {
      classes += sizeClasses[responsiveSize.mobile];
    }

    // Tablet (sm breakpoint)
    if (responsiveSize.tablet) {
      classes += ` sm:${sizeClasses[responsiveSize.tablet].replace(/\s+/g, ' sm:')}`;
    }

    // Desktop (md breakpoint)
    if (responsiveSize.desktop) {
      classes += ` md:${sizeClasses[responsiveSize.desktop].replace(/\s+/g, ' md:')}`;
    }

    return classes || sizeClasses[size];
  };

  const classes = cn(
    baseClasses,
    variantClasses[variant],
    getResponsiveSizeClasses(),
    fullWidth && 'w-full',
    className
  );

  const parallelogramBg = variant === 'primary'
    ? 'bg-primary group-hover:bg-white active:bg-white cursor-pointer '
    : 'bg-white hover:bg-gray-50 active:bg-gray-100 border border-gray-300 hover:border-primary active:border-primary cursor-pointer';

  const content = (
    <>
      {/* Parallelogram background */}
      <div
        className={cn(
          'absolute inset-0 z-0 transform cursor-pointer -skew-x-26  group-hover:skew-x-0 transition-all duration-300 ease-in-out',
          parallelogramBg
        )}
      />
      {/* Animated border lines */}
      <div className="pointer-events-none absolute inset-0 z-[5]">
        {/* Top */}
        <span className="absolute left-0 right-0 top-0 h-0.5 bg-primary transform scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100 opacity-0 group-hover:opacity-60" />
        {/* Right */}
        <span className="absolute right-0 top-0 bottom-0 w-0.5 bg-primary transform scale-y-0 origin-top transition-transform duration-300 delay-75 group-hover:scale-y-100 opacity-0 group-hover:opacity-60" />
        {/* Bottom */}
        <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-primary transform scale-x-0 origin-right transition-transform duration-300 delay-150 group-hover:scale-x-100 opacity-0 group-hover:opacity-60" />
        {/* Left */}
        <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary transform scale-y-0 origin-bottom transition-transform duration-300 delay-200 group-hover:scale-y-100 opacity-0 group-hover:opacity-60" />
      </div>
      {/* Button content */}
      <span className="relative z-10 transform cursor-pointer flex items-center gap-2  whitespace-nowrap font-normal">
        {children}
      </span>
    </>
  );

  if (href) {
    return (
      <Link href={href} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {content}
    </button>
  );
};

export default Button;